// "Zamówienia w wyniku" (owner preview 2026-10-26): every ZK of the period in
// one row — in the actual (approved sales invoice), in the forecast (waiting
// for an invoice), both, or outside the sums. Pure folding of the income
// statement response: membership comes from the backend flags and the WP6
// `forecastVsActual` rows (order-margin basis), never recomputed here. The
// rows are the money of the Rachunek wyników lines (decision
// 2026-10-29-zysk-przy-sprzedazy), so a tile here is the sum of its rows: an
// incomplete cost counts with its known part and the row is flagged.
import type {
  ForecastVsActualOrder,
  IncomeStatementResponse,
} from './incomeStatementApi';
import { addLedgerMonth, addPlanPending, emptyPendingScope, isScopePending, sumDeltas, type PendingDeltas } from '../pendingScope';

export type OrderPlace = 'ACTUAL' | 'FORECAST' | 'BOTH' | 'OUTSIDE';

/** Display-only hints; none of them changes an amount. */
export type OrderResultFlag =
  | 'COST_UNKNOWN'
  | 'NO_COST'
  | 'PAST_FORECAST'
  | 'WITHDRAWN'
  | 'AWAITING_INVOICE';

export interface OrderResultPart {
  revenue: number;
  /** Margin of the known cost; null = unknown cost (never 0). */
  margin: number | null;
  /** Some of the cost is only its known part (goods not issued, no average). */
  costPending?: boolean;
  months: string[];
  /** The same money per month (the pivot's period columns); sums to the part. */
  byMonth: OrderResultMonth[];
}

/** One month of a part; `pendingDeltas` (actual) let any sum of months decide its „*”. */
export interface OrderResultMonth {
  month: string;
  revenue: number;
  margin: number | null;
  costPending?: boolean;
  pendingDeltas?: PendingDeltas;
}

export interface OrderInResultRow {
  id: string;
  displayOrderNumber: string;
  customerName: string;
  status: string;
  place: OrderPlace;
  actual: OrderResultPart | null;
  forecast: OrderResultPart | null;
  flags: OrderResultFlag[];
  reviewReasons: string[];
}

export interface OrdersInResultTotals {
  actual: { count: number; revenue: number; margin: number; unknownCostCount: number };
  forecast: { count: number; revenue: number; margin: number; unknownCostCount: number };
  outsideCount: number;
  /** Distinct ZK in the actual or the forecast (an order in both counts once). */
  totalCount: number;
  /** Of them, ZK with an incomplete cost after the period is summed (`pendingScope.ts`). */
  totalPendingCount: number;
}

const EPSILON = 0.5;

/**
 * A part's „*” from its months (one rule for every range, `pendingScope.ts`):
 * actual = the ledger changes summed over the months, forecast = any month
 * with a plan of an incomplete cost.
 */
export function monthsCostPending(slots: OrderResultMonth[], basis: 'actual' | 'forecast', orderId = 'order'): boolean {
  const scope = emptyPendingScope();
  slots.forEach((slot) => {
    if (basis === 'actual') addLedgerMonth(scope, orderId, slot.pendingDeltas, Boolean(slot.costPending) || slot.margin == null);
    else if (slot.costPending || slot.margin == null) addPlanPending(scope, orderId);
  });
  return isScopePending(scope);
}

function fold(rows: ForecastVsActualOrder[] | undefined, basis: 'actual' | 'forecast'): Map<string, OrderResultPart> {
  const byId = new Map<string, OrderResultPart>();
  for (const row of rows || []) {
    const part = byId.get(row.id) || { revenue: 0, margin: 0, costPending: false, months: [], byMonth: [] };
    const pending = row.costPending || row.margin == null;
    part.revenue += row.revenue || 0;
    part.margin = part.margin == null || row.margin == null ? null : part.margin + row.margin;
    if (row.month && !part.months.includes(row.month)) part.months.push(row.month);
    const month = row.month || '';
    const slot = part.byMonth.find((entry) => entry.month === month);
    if (slot) {
      slot.revenue += row.revenue || 0;
      slot.margin = slot.margin == null || row.margin == null ? null : slot.margin + row.margin;
      slot.costPending = Boolean(slot.costPending) || pending;
      slot.pendingDeltas = sumDeltas(slot.pendingDeltas, row.pendingDeltas);
    } else {
      part.byMonth.push({
        month, revenue: row.revenue || 0, margin: row.margin, costPending: pending,
        ...(row.pendingDeltas ? { pendingDeltas: { ...row.pendingDeltas } } : {}),
      });
    }
    byId.set(row.id, part);
  }
  byId.forEach((part, id) => {
    part.months.sort();
    part.costPending = monthsCostPending(part.byMonth, basis, id);
    // A row without a month counts in the part, not in any month.
    part.byMonth = part.byMonth.filter((entry) => entry.month).sort((a, b) => a.month.localeCompare(b.month));
  });
  return byId;
}

// "N/MM/YYYY" → sortable "YYYY-MM-NNNN"; anything else sorts last.
function numberKey(displayOrderNumber: string): string {
  const match = /^(\d+)\/(\d{2})\/(\d{4})$/.exec(displayOrderNumber.trim());
  return match ? `${match[3]}-${match[2]}-${match[1].padStart(4, '0')}` : `0000-${displayOrderNumber}`;
}

export function buildOrdersInResult(statement: IncomeStatementResponse): OrderInResultRow[] {
  const actualById = fold(statement.forecastVsActual?.orders.actual, 'actual');
  const forecastById = fold(statement.forecastVsActual?.orders.forecast, 'forecast');
  const rows: OrderInResultRow[] = [];

  for (const customer of statement.customers) {
    for (const order of customer.orders) {
      const flags: OrderResultFlag[] = [];
      const actualPart = actualById.get(order.id) || null;
      // A cost catch-up without revenue is still money of the result.
      const actual = actualPart && (Math.abs(actualPart.revenue) > EPSILON || Math.abs(actualPart.margin ?? 0) > EPSILON) ? actualPart : null;
      // An invoice and its withdrawal cancel out: in the actual by the flag, 0 zł in total.
      if (order.inActual && !actual) flags.push('WITHDRAWN');
      const forecastPart = forecastById.get(order.id) || null;
      const forecast = forecastPart && forecastPart.revenue > EPSILON ? forecastPart : null;
      if ((actual && actual.costPending) || (forecast && forecast.costPending)) flags.push('COST_UNKNOWN');
      if (forecast && forecast.margin != null && Math.abs(forecast.margin - forecast.revenue) < EPSILON) flags.push('NO_COST');
      if (forecast && forecast.months.some((month) => month < statement.currentMonth)) flags.push('PAST_FORECAST');
      if (order.awaitingSettlement) flags.push('AWAITING_INVOICE');
      const place: OrderPlace = actual && forecast ? 'BOTH' : actual ? 'ACTUAL' : forecast ? 'FORECAST' : 'OUTSIDE';
      rows.push({
        id: order.id,
        displayOrderNumber: order.displayOrderNumber,
        customerName: customer.name,
        status: order.status,
        place,
        actual,
        forecast,
        flags,
        reviewReasons: [],
      });
    }
  }

  const listed = new Set(rows.map((row) => row.id));
  for (const order of statement.outsidePlan.orders) {
    if (listed.has(order.id)) continue;
    rows.push({
      id: order.id,
      displayOrderNumber: order.displayOrderNumber,
      customerName: order.customerName,
      status: '',
      place: 'OUTSIDE',
      actual: null,
      forecast: null,
      flags: [],
      reviewReasons: order.reviewReasons,
    });
  }

  return rows.sort((a, b) => numberKey(b.displayOrderNumber).localeCompare(numberKey(a.displayOrderNumber)));
}

export function ordersInResultTotals(rows: OrderInResultRow[]): OrdersInResultTotals {
  const totals: OrdersInResultTotals = {
    actual: { count: 0, revenue: 0, margin: 0, unknownCostCount: 0 },
    forecast: { count: 0, revenue: 0, margin: 0, unknownCostCount: 0 },
    outsideCount: 0,
    totalCount: 0,
    totalPendingCount: 0,
  };
  for (const row of rows) {
    if (row.actual) {
      totals.actual.count += 1;
      totals.actual.revenue += row.actual.revenue;
      // The known part, like the Rachunek wyników line; the counter says how
      // many orders have an incomplete cost.
      // An unknown cost counts its known part 0 (margin = revenue), like the pivot.
      totals.actual.margin += row.actual.margin ?? row.actual.revenue;
      if (row.actual.costPending) totals.actual.unknownCostCount += 1;
    }
    if (row.forecast) {
      totals.forecast.count += 1;
      totals.forecast.revenue += row.forecast.revenue;
      totals.forecast.margin += row.forecast.margin ?? row.forecast.revenue;
      if (row.forecast.costPending) totals.forecast.unknownCostCount += 1;
    }
    if (row.place === 'OUTSIDE') totals.outsideCount += 1;
    if (row.actual || row.forecast) totals.totalCount += 1;
    if (row.actual?.costPending || row.forecast?.costPending) totals.totalPendingCount += 1;
  }
  return totals;
}

function partInMonths(part: OrderResultPart | null, months: Set<string>, basis: 'actual' | 'forecast', id: string): OrderResultPart | null {
  const slots = (part?.byMonth || []).filter((entry) => months.has(entry.month));
  if (slots.length === 0) return null;
  return {
    revenue: slots.reduce((sum, slot) => sum + slot.revenue, 0),
    margin: slots.reduce<number | null>((sum, slot) => (sum == null || slot.margin == null ? null : sum + slot.margin), 0),
    costPending: monthsCostPending(slots, basis, id),
    months: slots.map((slot) => slot.month),
    byMonth: slots,
  };
}

/**
 * The orders of some months (the period of „Wynik firmy”: a quarter or a
 * month of the loaded year): each row keeps only those months' actual and
 * forecast money, its „*” decided after they are summed; rows without money
 * there and orders outside the sums drop out (they have no month).
 */
export function rowsInMonths(rows: OrderInResultRow[], months: Iterable<string>): OrderInResultRow[] {
  const set = new Set(months);
  return rows.flatMap((row) => {
    const actual = partInMonths(row.actual, set, 'actual', row.id);
    const forecast = partInMonths(row.forecast, set, 'forecast', row.id);
    if (!actual && !forecast) return [];
    const place: OrderPlace = actual && forecast ? 'BOTH' : actual ? 'ACTUAL' : 'FORECAST';
    return [{ ...row, place, actual, forecast }];
  });
}

/** One month (a month bar of „Pulpit zarządu”). */
export function rowsInMonth(rows: OrderInResultRow[], month: string): OrderInResultRow[] {
  return rowsInMonths(rows, [month]);
}
